import { Box } from "arkh";

import React, { useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";

import { graphql, useFragment } from "react-relay";

import { WidgetFile_widgetFile$key } from "./__generated__/WidgetFile_widgetFile.graphql";
import { useInterval } from "../../useInterval";
import { WidgetFilePage, WidgetFilePageProcessing } from "./WidgetFilePage";
import { WidgetFileStatic_widgetFile$key } from "./__generated__/WidgetFileStatic_widgetFile.graphql";
import { WidgetFileDynamic } from "./WidgetFileDynamic";
import { PageIndicator } from "./PageIndicator";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetFileSubscription } from "./__generated__/WidgetFileSubscription.graphql";

export const conf = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

export const WidgetFile = (
  props: {
    widget: WidgetFile_widgetFile$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetFile_widgetFile on WidgetFile {
        files {
          file {
            id
            status
          }
        }
        ...WidgetFileStatic_widgetFile
        ...WidgetFileDynamic_widgetFile
      }
    `,
    props.widget
  );

  const fileIds = useMemo(
    () => widget.files.map((f) => f.file.id),
    [widget.files]
  );
  useSubscriptionWithSkip<WidgetFileSubscription>(
    useMemo(
      () => ({
        variables: {
          ids: fileIds,
        },
        subscription: graphql`
          subscription WidgetFileSubscription($ids: [ID!]!) {
            fileUpdate(ids: $ids) {
              fileWidgets {
                ...WidgetFileStatic_widgetFile
                ...WidgetFileDynamic_widgetFile
              }
            }
          }
        `,
      }),
      [fileIds]
    ),
    {
      skip: fileIds.length === 0,
    }
  );

  const { widget: _, ...rest } = props;
  if (props.idiom === "STATIC") {
    return <WidgetFileStatic widget={widget} {...rest} />;
  } else {
    return <WidgetFileDynamic widget={widget} {...rest} />;
  }
};

function noOp() {}

export function WidgetFileStatic(props: {
  widget: WidgetFileStatic_widgetFile$key;
  pageId?: string;
  isFocusMode?: boolean;
}) {
  const widget = useFragment(
    graphql`
      fragment WidgetFileStatic_widgetFile on WidgetFile {
        id
        title
        slideshowDuration
        files {
          pages {
            id
            ...WidgetFilePage_page
          }
          file {
            id
            status
            ...WidgetFilePageProcessing_file
            name
          }
        }
      }
    `,
    props.widget
  );

  type ListItemType =
    | {
        fileId: string;
        type: "PAGE";
        page: (typeof widget.files)[number]["pages"][number];
      }
    | {
        fileId: string;
        type: "PROCESSING";
        file: (typeof widget.files)[number]["file"];
      };

  const listData = useMemo(
    () =>
      widget.files.flatMap<ListItemType>((f) =>
        f.file.status === "READY"
          ? f.pages.map((p) => ({
              fileId: f.file.id,
              page: p,
              type: "PAGE" as const,
            }))
          : [{ type: "PROCESSING" as const, file: f.file, fileId: f.file.id }]
      ),
    [widget]
  );

  const [_index, setIndex] = React.useState(0);

  const index = useMemo(() => {
    if (props.isFocusMode) {
      if (props.pageId) {
        const found = listData.findIndex(
          (page) => page.type === "PAGE" && page.page.id === props.pageId
        );
        if (found >= 0) {
          return found;
        }
      }
      return 0;
    }
    return _index;
  }, [_index, listData, props.isFocusMode, props.pageId]);

  if (!props.isFocusMode) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInterval(() => {
      setIndex((prevIndex) =>
        listData.length === 0 ? 0 : (prevIndex + 1) % listData.length
      );
    }, (widget.slideshowDuration ?? 15) * 1000);
  }
  const currentPage = listData[index];

  if (!currentPage) {
    return null;
  }
  return (
    <>
      <Box
        color="surface-backgroundControl"
        cornerRadius="medium"
        style={{
          flex: 1,
          overflow: "hidden",
        }}
      >
        {currentPage.type === "PROCESSING" ? (
          <WidgetFilePageProcessing file={currentPage.file} />
        ) : (
          <WidgetFilePage
            fileId={currentPage.fileId}
            onPress={noOp}
            page={currentPage.page}
          />
        )}
      </Box>
      {listData.length > 1 ? (
        <PageIndicator
          currentPage={index + 1}
          numberOfItems={listData.length}
        />
      ) : null}
    </>
  );
}
